import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Breadcrumbs } from "@mui/material";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../components/TextField"
import Dropdown from "../../../components/Dropdown";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import { getAllMachines } from "../../machine/api";
import { MachineData } from "../../machine/interface";
import { getVersionsByMachineId } from "../../machine/version/api";
import { MachineVersionData ,ApiResponse as VersionApiResponse} from "../../machine/version/interface";
import { getMachinesByCompanyID, attachMachine, detachMachine } from './api'
import { ApiResponse, MachineParams, CommpanyMachineData } from './interface'
import { getErrorMessage } from "../../../utils/error";

const Machine: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { company_id } = useParams();
    const company_name = location.state?.company_name || "Company";
    const [companyMachines, setCompanyMachines] = useState<CommpanyMachineData[]>([]);
    const [machines, setMachines] = useState<MachineData[]>([]);
    const [versions, setVersions] = useState<MachineVersionData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        delete: false,
        versions: false,
    });

    const validationSchema = Yup.object().shape({
        machineId: Yup.string().required("Machine is required"),
        machineVersionId: Yup.string().required("Machine version is required"),
    });

    const columns: GridColDef[] = [
        { field: "machine_name", headerName: "Machine Name", flex: 1 },
        { field: "machine_code", headerName: "Machine Code", flex: 1 },
        { field: "version", headerName: "Version", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                        {
                            icon: <PrecisionManufacturingIcon color="success" />,
                            tooltip: "Instance",
                            onClick: handleViewCompanyMachineInstance,
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchMachines = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<CommpanyMachineData[]> = await getMachinesByCompanyID(company_id || "");
                if (res.status && res.data) {
                    setCompanyMachines(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch machines.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching machines");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchMachines();
    }, [company_id]);

    const openDialog = async () => {
        setDialogOpen(true)
        setLoading(prev => ({ ...prev, fetch: true }));
        try {
            const res: ApiResponse<MachineData[]> = await getAllMachines();
            if (res.status && res.data) {
                setMachines(res.data);
            } else {
                errorMessage(res.message || "Failed to fetch machines.");
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Error fetching machines");
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setVersions([]);
        setDialogOpen(false);
    };

    const handleMachineChange = async (machineId: string, setFieldValue: (field: string, value: any) => void) => {
        setFieldValue("machineId", machineId);
        setFieldValue("machineVersionId", "");
        setVersions([]);

        if (!machineId) return;

        setLoading(prev => ({ ...prev, versions: true }));
        try {
            const res: VersionApiResponse<MachineVersionData[]> = await getVersionsByMachineId(machineId);
            if (res.status && res.data) {
                setVersions(res.data);
            } else {
                errorMessage(res.message || "Failed to fetch versions.");
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Error fetching machine versions");
        } finally {
            setLoading(prev => ({ ...prev, versions: false }));
        }
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleViewCompanyMachineInstance = (params: GridCellParams) => {
        const company_machine_id = params.row.company_machine_id;
        const machine_name = params.row.machine_name;
        const machine_id = params.row.machine_id;
        navigate(`/company/${company_id}/machines/${company_machine_id}`, {
            state: { company_name, machine_name, machine_id }
        });
    };

    const confirmDialog = async (values: any, { resetForm }: { resetForm: () => void }) => {
        const success = await handleCreate(values);

        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newMachine: MachineParams = {
                machine_id: values.machineId,
                machine_version_id: values.machineVersionId,
                company_id: company_id!,
            };
            const response = await attachMachine(newMachine);
            if (response.status && response.data) {
                const machineData = machines.find(machine => machine.machine_id === values.machineId);
                const versionData = versions.find(v => v.machine_version_id === values.machineVersionId);
                const machine = {
                    company_machine_id: response.data.company_machine_id,
                    machine_id: values.machineId,
                    machine_version_id: values.machineVersionId,
                    machine_name: machineData?.machine_name || "",
                    machine_code: machineData?.machine_code || "",
                    version: versionData?.version || "",
                };
                setCompanyMachines(prev => [...prev, machine]);
                successMessage(response.message);
                return true;
            } else {
                errorMessage(response.message || "Failed to attach machine.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const response = await detachMachine(itemToDelete.row.company_machine_id);
                if (response.status) {
                    setCompanyMachines(prev => prev.filter(m => m.company_machine_id !== itemToDelete.row.company_machine_id));
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || "Failed to delete machine.");
                }
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/company">
                                    Company
                                </Link>
                                <Link color="primary" to={`/company`}>{company_name}</Link>
                                <Link color="primary" to={`/company/${company_id}/machines`} state={{ company_name: company_name }}>
                                    Machines
                                </Link>
                            </Breadcrumbs>
                        </div>
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                            <GridTable
                                rows={companyMachines}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.company_machine_id}
                            />
                        </div>
                        <Formik
                            initialValues={{
                                machineId: "",
                                machineVersionId: "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, setFieldValue, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={"Attach New Machine"}
                                    confirmButtonText={"Attach"}
                                    cancelButtonText="Cancel"
                                    icon={<MiscellaneousServicesIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={6}>
                                                            <Dropdown
                                                                options={machines.map((machine) => ({
                                                                    id: machine.machine_id,
                                                                    name: `${machine.machine_name} (${machine.machine_code})`,
                                                                }))}
                                                                value={values.machineId ?? ""}
                                                                onChange={(id) => handleMachineChange(String(id), setFieldValue)}
                                                                label="Machine"
                                                                required
                                                                error={Boolean(touched.machineId && errors.machineId)}
                                                                helperText={
                                                                    touched.machineId && errors.machineId
                                                                        ? String(errors.machineId)
                                                                        : undefined
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={6}>
                                                            <Dropdown
                                                                options={versions.map((version) => ({
                                                                    id: version.machine_version_id,
                                                                    name: version.version,
                                                                }))}
                                                                value={values.machineVersionId ?? ""}
                                                                onChange={(id) => setFieldValue("machineVersionId", id)}
                                                                label="Machine Version"
                                                                required
                                                                disabled={!values.machineId || loading.versions}
                                                                error={Boolean(touched.machineVersionId && errors.machineVersionId)}
                                                                helperText={
                                                                    touched.machineVersionId && errors.machineVersionId
                                                                        ? String(errors.machineVersionId)
                                                                        : loading.versions
                                                                            ? "Loading versions..."
                                                                            : undefined
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                    </main>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Dettach Machine" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to dettach this machine?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Machine;